import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { type CSSProperties, type ReactNode } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import Icon from "../../Icon";
import { formatLocalizedCurrency } from "utils";

interface ReactTableProps<TData> {
  style?: CSSProperties;
  data: TData[];
  columns: any[];
  getRowColorKey?: (row: TData) => string | number;
  fontSize?: "xs" | "sm" | "md" | "lg" | string; // Predefined sizes or custom value
  hideHeader?: boolean;
  topRow?: ReactNode;
}

// Persistent color assignment per key
interface RowColor {
  backgroundColor: string;
}

export const getRowColorByKey = (() => {
  const colorMap = new Map<string | number, RowColor>();
  const colors = ["#E1E1E1", "#EEE"];

  let toggle = 0;

  return (key: string | number): RowColor => {
    if (!colorMap.has(key)) {
      const backgroundColor = colors[toggle % 2];
      toggle++;
      colorMap.set(key, { backgroundColor });
    }
    return colorMap.get(key)!;
  };
})();

// Helper to get CSS variable or custom value
const getFontSizeValue = (fontSize?: string): string | undefined => {
  if (!fontSize) return undefined;

  const fontSizeMap: Record<string, string> = {
    xs: "var(--font-xs)",
    sm: "var(--font-sm)",
    md: "var(--font-md)",
    lg: "var(--font-lg)",
  };

  return fontSizeMap[fontSize] || fontSize;
};

const ReactTable = <TData extends object>({
  data,
  columns,
  style,
  fontSize,
  getRowColorKey,
  hideHeader = false,
  topRow,
}: ReactTableProps<TData>) => {
  const { state } = useLocation();
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true,
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const handleSortClick = ({
    colName,
    orderBy,
  }: {
    colName: string;
    orderBy: string;
  }) => {
    setSearchParams(
      (searchParams) => {
        searchParams.set("sortBy", colName);
        searchParams.set("orderBy", orderBy === "ASC" ? "DESC" : "ASC");
        searchParams.set("page", "1");
        return searchParams;
      },
      { replace: true, state },
    );
  };

  const sortBy = searchParams.get("sortBy") ?? "";
  const orderBy = searchParams.get("orderBy") ?? "";

  const fontSizeValue = getFontSizeValue(fontSize || "sm");

  return (
    <div
      className="table__container"
      style={{
        ...style,
        fontSize: fontSizeValue,
      }}
    >
      <table className="table">
        {!hideHeader && (
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="table__head__row">
                {headerGroup.headers.map((header) => {
                  const meta = header.column.columnDef.meta;

                  const isRightAligned =
                    meta?.type === "currency" || meta?.align === "right";

                  const width =
                    header.getSize() !== 150 ? header.getSize() : undefined;
                  return (
                    <th
                      key={header.id}
                      style={{
                        width,
                        textAlign: isRightAligned ? "right" : "left",
                      }}
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          className="heading__container"
                          onClick={() =>
                            header.column.columnDef.enableSorting &&
                            handleSortClick({ colName: header.id, orderBy })
                          }
                          style={{
                            whiteSpace: "nowrap",
                            cursor: header.column.columnDef.enableSorting
                              ? "pointer"
                              : "default",
                            justifyContent:
                              isRightAligned
                                ? "flex-end"
                                : "flex-start",
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          {header.column.columnDef.enableSorting && (
                            <div className="arrow__buttons__container">
                              <div className="icon__button">
                                <Icon
                                  name="upArrow"
                                  color={`${sortBy === header.id && orderBy === "ASC"
                                    ? "var(--table-header-icon--active)"
                                    : "var(--table-header-icon--inactive)"
                                    }`}
                                  size={18}
                                />
                              </div>
                              <div className="icon__button">
                                <Icon
                                  name="downArrow"
                                  color={`${sortBy === header.id && orderBy === "DESC"
                                    ? "var(--table-header-icon--active)"
                                    : "var(--table-header-icon--inactive)"
                                    }`}
                                  size={18}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
        )}
        <tbody>
          {topRow}
          {table.getRowModel().rows.map((row) => {
            const rowData = row.original;
            const rowKey = getRowColorKey ? getRowColorKey(rowData) : null;
            const { backgroundColor } = rowKey
              ? getRowColorByKey(rowKey)
              : { backgroundColor: "transparent" };
            return (
              <tr
                key={row.id}
                className="table__body__row"
                style={{
                  backgroundColor,
                  color: rowKey ? "#000" : "inherit",
                  userSelect: "text",
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="table_data"
                    style={{
                      textAlign:
                        cell.column.columnDef.meta?.align ||
                        (cell.column.columnDef.meta?.type === "number" ||
                          cell.column.columnDef.meta?.type === "currency"
                          ? "right"
                          : "left"),
                    }}
                  >
                    {(() => {
                      const value = cell.getValue();
                      const meta = cell.column.columnDef.meta;

                      if (
                        meta?.type === "currency" &&
                        value &&
                        !isNaN(Number(value))
                      ) {
                        return formatLocalizedCurrency(value);
                      }

                      return flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      );
                    })()}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ReactTable;
